import { Toaster } from "sonner";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import "@/App.css";
import { AuthProvider, useAuth, isAdmin, getAuthenticatedLandingPath } from "@/lib/auth.jsx";

import AppShell from "@/components/AppShell";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Quotes from "@/pages/Quotes";
import ReadyToQuote from "@/pages/ReadyToQuote";
import QuoteBuilder from "@/pages/QuoteBuilder";
import QuoteView from "@/pages/QuoteView";
import HomeInventory from "@/pages/HomeInventory";
import HomeCatalog from "@/pages/HomeCatalog";
import PropertyPackages from "@/pages/PropertyPackages";
import SettingsPage from "@/pages/Settings";
import Integrations from "@/pages/Integrations";
import PublicQuote from "@/pages/PublicQuote";

function Protected({ children }) {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center text-slate-500">
                Loading...
            </div>
        );
    }

    if (!user) return <Navigate to="/login" replace />;

    return children;
}

function AdminOnly({ children }) {
    const { user } = useAuth();

    if (!isAdmin(user)) return <Navigate to={getAuthenticatedLandingPath(user)} replace />;

    return children;
}

function RedirectToQuotes() {
    return <Navigate to="/quotes" replace />;
}

function AppRoutes() {
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/quote/:shareToken" element={<PublicQuote />} />
            <Route
                path="/*"
                element={
                    <Protected>
                        <AppShell>
                            <RouteErrorBoundary>
                                <Routes>
                                <Route path="/" element={<Dashboard />} />
                                <Route path="/dashboard" element={<Dashboard />} />
                                <Route path="/ready-to-quote" element={<ReadyToQuote />} />
                                <Route path="/quotes/ready" element={<ReadyToQuote />} />
                                <Route path="/quotes" element={<Quotes />} />
                                <Route path="/quotes/new" element={<QuoteBuilder />} />
                                <Route path="/quotes/:id/edit" element={<QuoteBuilder />} />
                                <Route path="/quotes/:id" element={<QuoteView />} />
                                <Route path="/home-inventory" element={<HomeInventory />} />
                                <Route path="/inventory" element={<HomeInventory />} />
                                <Route path="/home-catalog" element={<HomeCatalog />} />
                                <Route path="/homes" element={<HomeCatalog />} />
                                <Route path="/property-packages" element={<PropertyPackages />} />
                                <Route path="/properties" element={<PropertyPackages />} />
                                <Route path="/leads" element={<RedirectToQuotes />} />
                                <Route path="/leads/:id" element={<RedirectToQuotes />} />
                                <Route path="/sales-crm" element={<RedirectToQuotes />} />
                                <Route path="/projects" element={<RedirectToQuotes />} />
                                <Route path="/projects/:id" element={<RedirectToQuotes />} />
                                <Route path="/customers" element={<RedirectToQuotes />} />
                                <Route path="/customers/:id" element={<RedirectToQuotes />} />
                                <Route path="/key-contacts" element={<RedirectToQuotes />} />
                                <Route
                                    path="/integrations"
                                    element={
                                        <AdminOnly>
                                            <Integrations />
                                        </AdminOnly>
                                    }
                                />
                                <Route path="/integrations/quickbooks" element={<Navigate to="/integrations" replace />} />
                                <Route
                                    path="/settings"
                                    element={
                                        <AdminOnly>
                                            <SettingsPage />
                                        </AdminOnly>
                                    }
                                />
                                <Route path="*" element={<Navigate to="/dashboard" replace />} />
                                </Routes>
                            </RouteErrorBoundary>
                        </AppShell>
                    </Protected>
                }
            />
        </Routes>
    );
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <AppRoutes />
                    <Toaster position="top-right" richColors closeButton />
                </BrowserRouter>
            </AuthProvider>
        </div>
    );
}

export default App;
